/**
 * MCP Command
 * Manage local and cloud MCP server configurations
 */

import { Command } from 'commander';
import chalk from 'chalk';
import { readFileSync, writeFileSync, existsSync } from 'fs';
import { join } from 'path';
import { getApiClient } from '../api/client.js';
import { checkAuthentication } from '../agent/utils.js';
import { McpClient } from '../mcp/client.js';
import { getConfigDir } from '../utils/config.js';

function getMcpConfigPath(): string {
    return join(getConfigDir(), 'mcp_config.json');
}

interface McpServerConfig {
    command: string;
    args: string[];
    env?: Record<string, string>;
}

interface McpConfigFile {
    mcpServers: Record<string, McpServerConfig>;
}

function loadLocalConfig(): McpConfigFile {
    if (!existsSync(getMcpConfigPath())) {
        return { mcpServers: {} };
    }
    try {
        return JSON.parse(readFileSync(getMcpConfigPath(), 'utf-8'));
    } catch {
        return { mcpServers: {} };
    }
}

function saveLocalConfig(config: McpConfigFile): void {
    writeFileSync(getMcpConfigPath(), JSON.stringify(config, null, 2));
}

export const mcpCommand = new Command('mcp')
    .description('Manage local and cloud MCP server configurations')
    .action(() => {
        // Default: list
        listMcpServers();
    });

// Subcommand: mcp list
mcpCommand
    .command('list')
    .description('List configured MCP servers')
    .option('--cloud', 'Also show cloud MCP configs')
    .option('--json', 'Output as JSON')
    .action(async (options) => {
        await listMcpServers(options.cloud, options.json);
    });

// Subcommand: mcp add
mcpCommand
    .command('add <name>')
    .description('Add a new local MCP server')
    .requiredOption('-c, --command <command>', 'Command to run the server')
    .option('-a, --args <args>', 'Comma-separated arguments')
    .action((name: string, options) => {
        const config = loadLocalConfig();

        if (config.mcpServers[name]) {
            console.error(chalk.yellow(`Server "${name}" already exists. Use a different name or remove it first.`));
            process.exit(1);
        }

        config.mcpServers[name] = {
            command: options.command,
            args: options.args ? options.args.split(',').map((a: string) => a.trim()) : [],
        };

        saveLocalConfig(config);
        console.log(chalk.green(`Added MCP server: ${name}`));
        console.log(chalk.dim(`  Command: ${options.command}`));
        console.log(chalk.dim(`  Config: ${getMcpConfigPath()}`));
    });

// Subcommand: mcp remove
mcpCommand
    .command('remove <name>')
    .description('Remove a local MCP server')
    .action((name: string) => {
        const config = loadLocalConfig();

        if (!config.mcpServers[name]) {
            console.error(chalk.red(`Server "${name}" not found.`));
            process.exit(1);
        }

        delete config.mcpServers[name];
        saveLocalConfig(config);
        console.log(chalk.green(`Removed MCP server: ${name}`));
    });

// Subcommand: mcp test
mcpCommand
    .command('test <name>')
    .description('Test connection to an MCP server')
    .action(async (name: string) => {
        const config = loadLocalConfig();

        if (!config.mcpServers[name]) {
            console.error(chalk.red(`Server "${name}" not found in local config.`));
            process.exit(1);
        }

        console.log(chalk.dim(`Testing connection to ${name}...`));

        try {
            const client = new McpClient({ debug: false });
            const tools = await client.connect(name);
            console.log(chalk.green(`Connected to ${name}: ${tools.length} tools available`));

            if (tools.length > 0) {
                console.log(chalk.dim('\nAvailable tools:'));
                for (const tool of tools) {
                    console.log(chalk.dim(`  - ${tool.name}${tool.description ? `: ${tool.description}` : ''}`));
                }
            }

            await client.disconnectAll();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            console.error(chalk.red(`Failed to connect: ${errorMessage}`));
            process.exit(1);
        }
    });

// Subcommand: mcp cloud (list cloud configs)
mcpCommand
    .command('cloud')
    .description('List cloud MCP configurations')
    .option('--json', 'Output as JSON')
    .action(async (options) => {
        checkAuthentication();

        try {
            const client = getApiClient();
            const response = await client.get('/cloud-mcp');
            const configs = response.data.data || response.data.configs || response.data || [];

            if (options.json) {
                console.log(JSON.stringify(configs, null, 2));
                return;
            }

            if (!Array.isArray(configs) || configs.length === 0) {
                console.log(chalk.dim('No cloud MCP configurations found.'));
                return;
            }

            console.log(chalk.bold.blue('\nCloud MCP Configurations\n'));
            for (const cfg of configs) {
                console.log(`  ${chalk.cyan(cfg.name || cfg.id)} ${chalk.dim(cfg.description || '')}`);
            }
            console.log('');
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            console.error(chalk.red(`Failed to fetch cloud configs: ${errorMessage}`));
            process.exit(1);
        }
    });

async function listMcpServers(includeCloud = false, json = false): Promise<void> {
    const config = loadLocalConfig();
    const serverNames = Object.keys(config.mcpServers);

    if (json) {
        const result: Record<string, unknown> = { local: config.mcpServers };
        console.log(JSON.stringify(result, null, 2));
        return;
    }

    console.log(chalk.bold.blue('\nMCP Servers\n'));

    if (serverNames.length === 0) {
        console.log(chalk.dim('  No local MCP servers configured.'));
        console.log(chalk.dim(`  Config: ${getMcpConfigPath()}`));
    } else {
        console.log(chalk.dim('  Local:'));
        for (const name of serverNames) {
            const server = config.mcpServers[name];
            console.log(`    ${chalk.cyan(name)} ${chalk.dim(server.command)} ${chalk.dim(server.args.join(' '))}`);
        }
    }

    if (includeCloud) {
        try {
            const client = getApiClient();
            const response = await client.get('/cloud-mcp');
            const configs = response.data.data || response.data.configs || [];
            if (Array.isArray(configs) && configs.length > 0) {
                console.log(chalk.dim('\n  Cloud:'));
                for (const cfg of configs) {
                    console.log(`    ${chalk.cyan(cfg.name || cfg.id)}`);
                }
            }
        } catch {
            // Ignore cloud fetch errors in list
        }
    }

    console.log('');
    console.log(chalk.dim('  Add: paean mcp add <name> -c <command>'));
    console.log(chalk.dim('  Test: paean mcp test <name>'));
    console.log('');
}
